import React from "react";
import { Link } from "react-router-dom";

/**
 * Renders translated page copy stored in src/seo/pages.json.
 *
 * The copy is data, not JSX, so a locale can be added without touching components.
 * Inline markup is deliberately minimal — `[text](/path)` links and `**bold**` — which
 * is enough for this content and keeps internal links as real <Link> elements, so the
 * SPA still navigates client-side and crawlers still see the link graph.
 */

export type PageBlock =
    /** `id` gives the heading an anchor target so a `toc` block can link to it. */
    | { h2: string; id?: string }
    | { h3: string; id?: string }
    | { p: string }
    | { ul: string[] }
    | { ol: string[] }
    /** In-page navigation; `items` are inline links, e.g. `[Colors](#colors)`. */
    | { toc: { title: string; items: string[] } };

const TOKEN = /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*)/g;

/** Splits a string into text, links and bold runs. */
function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
    return text.split(TOKEN).filter(Boolean).map((part, i) => {
        const key = `${keyPrefix}-${i}`;

        const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
        if (link) {
            const [, label, href] = link;
            if (href.startsWith("/")) {
                return <Link key={key} to={href}>{label}</Link>;
            }
            // Same-page anchors must stay in the tab; only real off-site links open one.
            if (href.startsWith("#")) {
                return <a key={key} href={href}>{label}</a>;
            }
            return (
                <a key={key} href={href} target="_blank" rel="noopener noreferrer">
                    {label}
                </a>
            );
        }

        const bold = /^\*\*([^*]+)\*\*$/.exec(part);
        if (bold) return <strong key={key}>{bold[1]}</strong>;

        return <React.Fragment key={key}>{part}</React.Fragment>;
    });
}

export function PageBody({ blocks }: { blocks: PageBlock[] }) {
    return (
        <React.Fragment>
            {blocks.map((block, i) => {
                const key = `block-${i}`;
                if ("h2" in block) return <h2 key={key} id={block.id}>{renderInline(block.h2, key)}</h2>;
                if ("h3" in block) return <h3 key={key} id={block.id}>{renderInline(block.h3, key)}</h3>;
                if ("p" in block) return <p key={key}>{renderInline(block.p, key)}</p>;
                if ("toc" in block)
                    return (
                        <nav key={key} className="page-body__toc" aria-label={block.toc.title}>
                            <h2 className="page-body__toc-title">{block.toc.title}</h2>
                            <ul>
                                {block.toc.items.map((item, j) => (
                                    <li key={`${key}-${j}`}>{renderInline(item, `${key}-${j}`)}</li>
                                ))}
                            </ul>
                        </nav>
                    );
                if ("ul" in block)
                    return (
                        <ul key={key}>
                            {block.ul.map((item, j) => (
                                <li key={`${key}-${j}`}>{renderInline(item, `${key}-${j}`)}</li>
                            ))}
                        </ul>
                    );
                return (
                    <ol key={key}>
                        {block.ol.map((item, j) => (
                            <li key={`${key}-${j}`}>{renderInline(item, `${key}-${j}`)}</li>
                        ))}
                    </ol>
                );
            })}
        </React.Fragment>
    );
}
